/**
 * Reglas del formulario de checkout (CHK-03, CHK-04).
 *
 * Funciones puras: no tocan React ni el navegador, igual que las del carro. La
 * pantalla decide CUANDO mostrar un error; que algo sea un error se decide aca.
 *
 * Los dos bloques condicionales son el corazon de las dos historias: la
 * direccion se exige solo con despacho a domicilio (CHK-03) y los datos
 * tributarios solo con factura (CHK-04). Pedirlos siempre haria abandonar el
 * checkout a quien retira o a quien compra con boleta, que son la mayoria.
 *
 * La factura lleva ademas direccion tributaria (Random la exige, 29-09-2026):
 * con despacho, el comprador puede decir que es la misma; con retiro, la
 * escribe siempre.
 */
import type {
  DatosFacturacionDto,
  OpcionesDespacho,
  TipoDocumento,
  TipoEntrega,
} from '@elcarpintero/compartido';

import { alcanzaDespachoGratis } from './entrega';
import { rutEsValido } from './rut';

export interface DatosCheckout {
  nombre: string;
  email: string;
  telefono: string;

  tipoEntrega: TipoEntrega;
  /** Codigo de dos letras de la region; vacio mientras no se elija. */
  codigoRegion: string;
  comuna: string;
  calle: string;
  numero: string;
  departamento: string;
  referencia: string;

  tipoDocumento: TipoDocumento;
  /** RUT personal con boleta. Obligatorio: la boleta sale a su nombre. */
  rutComprador: string;
  /** RUT de la empresa, solo con factura. */
  rut: string;
  razonSocial: string;
  giro: string;
  /** "La direccion de facturacion es la misma que la de despacho". Solo cuenta con despacho. */
  facturacionIgualDespacho: boolean;
  /** Codigo de dos letras, de las 16 regiones: no depende de a donde se despacha. */
  codigoRegionFacturacion: string;
  comunaFacturacion: string;
  calleFacturacion: string;
  numeroFacturacion: string;
  departamentoFacturacion: string;

  /** Consentimiento explicito para los correos promocionales futuros (MAIL-04). */
  aceptaPromociones: boolean;
}

export const CHECKOUT_INICIAL: DatosCheckout = {
  nombre: '',
  email: '',
  telefono: '',
  tipoEntrega: 'despacho',
  codigoRegion: '',
  comuna: '',
  calle: '',
  numero: '',
  departamento: '',
  referencia: '',
  tipoDocumento: 'boleta',
  rutComprador: '',
  rut: '',
  razonSocial: '',
  giro: '',
  facturacionIgualDespacho: true,
  codigoRegionFacturacion: '',
  comunaFacturacion: '',
  calleFacturacion: '',
  numeroFacturacion: '',
  departamentoFacturacion: '',
  aceptaPromociones: false,
};

export type CampoCheckout = keyof DatosCheckout;

export type ErroresCheckout = Partial<Record<CampoCheckout, string>>;

/** Suficiente para atajar un tipeo; el correo real se prueba enviandolo. */
const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validarCheckout(datos: DatosCheckout): ErroresCheckout {
  const errores: ErroresCheckout = {};

  if (datos.nombre.trim().length < 3) errores.nombre = 'Escribe tu nombre y apellido';
  if (!CORREO.test(datos.email.trim())) errores.email = 'Revisa el correo';

  // Solo se cuentan los digitos: da lo mismo si viene con +56, espacios o guiones.
  if (datos.telefono.replace(/\D/g, '').length < 8) errores.telefono = 'Revisa el teléfono';

  if (datos.tipoEntrega === 'despacho') {
    if (datos.codigoRegion === '') errores.codigoRegion = 'Elige tu región';
    if (datos.comuna === '') errores.comuna = 'Elige tu comuna';
    if (datos.calle.trim().length < 3) errores.calle = 'Escribe la calle';
    if (datos.numero.trim().length === 0) errores.numero = 'Falta el número';
  }

  // Obligatorio con boleta (pedido del cliente, 10-10-2026) y valido: un tipeo
  // le llegaria a Random como el RUT de otra persona.
  if (datos.tipoDocumento === 'boleta') {
    if (datos.rutComprador.trim().length === 0) errores.rutComprador = 'Escribe tu RUT';
    else if (!rutEsValido(datos.rutComprador)) errores.rutComprador = 'El RUT no es válido';
  }

  if (datos.tipoDocumento === 'factura') {
    if (!rutEsValido(datos.rut)) errores.rut = 'El RUT no es válido';
    if (datos.razonSocial.trim().length < 3) errores.razonSocial = 'Escribe la razón social';
    if (datos.giro.trim().length < 3) errores.giro = 'Escribe el giro';

    if (pideDireccionFacturacion(datos)) {
      if (datos.codigoRegionFacturacion === '') errores.codigoRegionFacturacion = 'Elige la región';
      if (datos.comunaFacturacion === '') errores.comunaFacturacion = 'Elige la comuna';
      if (datos.calleFacturacion.trim().length < 3) errores.calleFacturacion = 'Escribe la calle';
      if (datos.numeroFacturacion.trim().length === 0) errores.numeroFacturacion = 'Falta el número';
    }
  }

  return errores;
}

/**
 * Si hay que escribir la direccion de facturacion: siempre con retiro (no hay
 * otra de donde copiarla) y con despacho solo si no es la misma.
 */
export function pideDireccionFacturacion(datos: DatosCheckout): boolean {
  return (
    datos.tipoDocumento === 'factura' &&
    (datos.tipoEntrega === 'retiro' || !datos.facturacionIgualDespacho)
  );
}

/**
 * Los datos de factura para el pedido. La direccion viaja solo si se escribio:
 * "la misma de despacho" la resuelve el servidor copiando la de entrega.
 */
export function facturacionDelPedido(datos: DatosCheckout): DatosFacturacionDto {
  const escrita = pideDireccionFacturacion(datos);

  return {
    rut: datos.rut,
    razonSocial: datos.razonSocial,
    giro: datos.giro,
    direccionIgualDespacho: !escrita,
    direccion: escrita
      ? {
          region: datos.codigoRegionFacturacion,
          comuna: datos.comunaFacturacion,
          calle: datos.calleFacturacion,
          numero: datos.numeroFacturacion,
          departamento: datos.departamentoFacturacion.trim() || null,
        }
      : null,
  };
}

/** La region de facturacion elegida, de las 16 del pais. */
export function regionFacturacionElegida(datos: DatosCheckout, opciones: OpcionesDespacho | null) {
  if (opciones === null || datos.codigoRegionFacturacion === '') return undefined;
  return opciones.geografia.find((region) => region.codigo === datos.codigoRegionFacturacion);
}

export function hayErrores(errores: ErroresCheckout): boolean {
  return Object.keys(errores).length > 0;
}

/** La region elegida, o `undefined` mientras no se elija ninguna. */
export function regionElegida(datos: DatosCheckout, opciones: OpcionesDespacho | null) {
  if (opciones === null || datos.codigoRegion === '') return undefined;
  return opciones.regiones.find((region) => region.codigo === datos.codigoRegion);
}

/** La comuna elegida, con su costo y su plazo ya resueltos por el servidor. */
export function comunaElegida(datos: DatosCheckout, opciones: OpcionesDespacho | null) {
  return regionElegida(datos, opciones)?.comunas.find((comuna) => comuna.nombre === datos.comuna);
}

/**
 * Lo que se le muestra al comprador por la entrega.
 *
 * El backend calcula lo mismo en `despacho.servicio.ts` y ES el que manda: esta
 * copia existe para que el total se actualice al elegir comuna sin ir al
 * servidor. La regla duplicada es una sola linea —desde cierto monto no se
 * cobra— y esta escrita en los dos lados a proposito; si cambia, cambia en los
 * dos.
 */
export function costoDespacho(
  datos: DatosCheckout,
  opciones: OpcionesDespacho | null,
  subtotal: number,
): number {
  if (datos.tipoEntrega === 'retiro' || opciones === null) return 0;
  if (alcanzaDespachoGratis(opciones, subtotal)) return 0;

  const comuna = comunaElegida(datos, opciones);
  if (comuna !== undefined) return comuna.costo;

  // Region elegida pero comuna todavia no: se muestra el costo base de la region.
  return regionElegida(datos, opciones)?.costo ?? 0;
}
